import {
  Autocomplete,
  Box,
  Button,
  Container,
  FormControl,
  FormLabel,
  Grid,
  MenuItem,
  Select,
  SelectChangeEvent,
  Tooltip,
  Typography
} from '@mui/material'
import { ConfirmDialog, Icon, SnackBarContext } from 'components'
import { TextField } from 'components/textField'
import { inject, observer } from 'mobx-react'
import { Course, Package, Program } from 'models'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router'
import {  Pages, RoutingParams } from 'routes'
import { RoutingStore } from 'stores'
import { DataStatus } from 'types'


export interface IPackageFormProps {
  course?:       Course,
  routingStore?: RoutingStore,
}

export const PackageForm = inject('routingStore')(observer((props: IPackageFormProps) => {
  const { t }     = useTranslation();
  const { toast } = React.useContext(SnackBarContext);
  const params: RoutingParams    = useParams<RoutingParams>();
  const { routingStore } = props;

  const [_package, setPackage]                    = React.useState<Package>(null);
  const [programs, setPrograms]                   = React.useState<Program[]>([]);
  const [programInputValue, setProgramInputValue] = React.useState<string>('');
  const [openConfirm,       setOpenConfirm]       = React.useState(false);

  const statusButtonAction = React.useCallback(async () => {
    const old_status:    DataStatus = _package.status;
    const target_status: DataStatus = _package.status === DataStatus.Published 
      ? DataStatus.Archived 
      : DataStatus.Published;

    if(await _package?.set('status', target_status, true)) toast(t('package_status_changed'), 'success');
    else {
      _package?.set('status', old_status, false);
      toast(t('toast_error_status_change_fail'), 'error');
    }
}, [_package]);


const statusButtonLabel = () => { 
    if (_package?.status === DataStatus.Draft)
      return t('publish');
    else if (_package?.status === DataStatus.Published)
      return t('archive');
    else if (_package?.status === DataStatus.Archived)
      return t('publish');
};

  React.useEffect(() => {
    Package.get(Number(params.id)).then(async (p: Package) => {
      setPackage(p);
      try {
        await p.loadPrograms();
      } catch (error) {
        console.error(error);
      }
    })
  }, [params.id]);

  React.useEffect(() => {
    Program.get().then((p: Program[]) => setPrograms(p))
  }, []);

  const years = [-1, 0, 1, 2, 3].map(y => new Date().getFullYear() + y);

  return (
    <Container maxWidth={'md'}>
      <Grid item xs={3} />
      <Grid item xs={6}>
        <Typography variant='h4' mb={3}>
          {t('package_settings')}
        </Typography>
        <FormControl fullWidth margin='normal'>
          <FormLabel>{t('package_name')}</FormLabel>
          <TextField
            debounced
            hiddenLabel
            value    = {_package?.title || ''}
            variant  = 'outlined'
            onChange = {(e) => _package.set('title', e.target.value, true)}
          />
        </FormControl>

        <FormControl fullWidth margin='normal'>
          <FormLabel>{t('package_description')}</FormLabel>
          <TextField
            hiddenLabel
            multiline
            debounced
            minRows     = '3'
            variant     = 'outlined'
            delay       = {300}
            placeholder = {t('package_description_placeholder')}
            value       = {_package?.desc || ''}
            onChange    = {(e: React.ChangeEvent<HTMLTextAreaElement>) =>
              _package.set('desc', e.target.value, true)
            }
          />
        </FormControl>

        <FormControl fullWidth margin='normal'>
          <FormLabel>{t('year')}</FormLabel>
          <Select 
            id          = "year"
            value       = {_package?.year || new Date().getFullYear()}
            onChange    = {(e: SelectChangeEvent<number>) =>
              _package.set('year', e.target.value, true)
            }
          >
            {years.map(y => (
              <MenuItem value={y}>{y}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl margin='normal' fullWidth>
          <FormLabel>{t('assign_programs')}</FormLabel>
          <Autocomplete
            multiple
            disableClearable
            limitTags            = {4}
            id                   = 'package-programs'
            options              = {programs}
            isOptionEqualToValue = {(option: Program, value: Program) =>
              option.id === value.id
            }
            getOptionLabel = {(option) => option.title}
            value          = {_package ? [..._package.programs] : []}  // This weird construction is necessary cause MUI otherwise triggers an error within the mobx observer proxy
            inputValue     = {programInputValue}
            noOptionsText  = {t('no_programs_found')}
            onClose        = {() => setProgramInputValue('')}
            onChange       = {async (event, values, reason, details) => {
              let succ = true;
              switch(reason) {
                case 'selectOption':
                  succ = await _package.linkProgram(details.option as Program);
                  break;
                case 'removeOption':
                  succ = await _package.unlinkProgram(details.option as Program);
                  break;
                case 'clear':
                  for(const p of _package.programs)
                    succ = succ && await _package.unlinkProgram(p);
                  break;
              }
              if(!succ) toast(t('failed_package_program_connection'), 'error');
            }}
            onInputChange  = {(event, value, reason) => {
              if (reason === 'reset') return;
              setProgramInputValue(value);
            }}
            renderInput   = {(params) => (
              <TextField
                {...params}
                hiddenLabel
                variant     = 'outlined'
                placeholder = {t('search_for_program')}
              />
            )}
          />
        </FormControl>
        <Box display='flex' justifyContent='space-between' mt={4}>
          <Button
            onClick   = {() => setOpenConfirm(true)}
            color     = 'secondary'
            variant   = 'outlined'
            startIcon = {<Icon iconId='bin' />}
          >
            {t('delete_package')}
          </Button>
          <Tooltip title={!_package?.canPublish ? t('publish_package_disabled') : ''}>
            <Box>
            <Button
              onClick  = {statusButtonAction}
              disabled = {!_package?.canPublish}
              variant  = 'contained'
            >
              {statusButtonLabel()} 
            </Button>
          </Box>
          </Tooltip>
          <ConfirmDialog
            title             = {t('confirm_delete_title') + '?'}
            open              = {openConfirm} 
            onCancel          = {() => setOpenConfirm(false)}
            content           = {t('confirm_delete_package_body')}
            confirmButtonText = {t('yes')}
            cancelButtonText  = {t('cancel')}
            onConfirm         = {async () => {
              if (await _package.remove())
                routingStore.push(Pages.Packages);
              else {
                console.error('Failed to remove package');
                toast(t('toast_error_remove_package'), 'error');
              }
            }}
          />
        </Box>
      </Grid>
      <Grid item xs={3} />
    </Container>
  );
}));
